import React from 'react';
import {
  Tooltip,
  TooltipProvider,
  TooltipTrigger,
  TooltipContent,
} from '@cargomatic/ui';
import { Info } from 'lucide-react';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { getDocumentsV2 } from 'utils/DocumentsAPIUtils';
import { getFileIconClass } from 'helpers/helper_functions';
import CargomaticSwitch from 'components/v2/Switch';
import formatMoney from 'utils/formatters/Money';
import { sluggify } from 'utils/localization/utils';

const LineItem = ({
  handlePODClick,
  handleToggleSelect,
  onGroupItem,
  lineItem,
  chargeCountMap,
  shipmentId,
  selected,
}) => {
  const [pod, setPod] = React.useState(null);
  const { t } = useTranslation();
  React.useEffect(() => {
    if (
      lineItem.category === 'base_cost' ||
      (lineItem.category === 'accessorials' &&
        lineItem.type === 'customer_adjustment')
    ) {
      getShipmentPOD(shipmentId);
    }
  }, []);

  const getFileIcon = (key) => {
    const extension = key.split('.').pop().toUpperCase();
    return getFileIconClass(extension);
  };
  const getShipmentPOD = async (shipment_id) => {
    const { data } = await getDocumentsV2([shipment_id], null, {
      order: {
        field: 'createdAt',
        direction: 'desc',
      },
      limit: 1,
    });

    const documents = data?.getDocumentsV2?.documents || [];
    const pods = documents.filter((doc) => doc.type === 'proof_of_delivery');
    const pocs = documents.filter((doc) => doc.type === 'proof_of_pickup');
    const docs = pods.length ? pods : pocs;
    setPod(docs[0] || null);
  };

  return (
    <div
      className="flex flex-wrap items-center justify-between mb-4"
      key={lineItem._id}
    >
      <div className="flex flex-wrap items-center w-full md:w-4/5">
        <input
          type="checkbox"
          className="leading-tight form-field"
          disabled={!lineItem.equipment}
          onChange={handleToggleSelect(lineItem)}
          checked={selected.includes(lineItem._id)}
        />
        <div className="flex items-center justify-between w-full ml-4 mr-2 md:w-4/5">
          <div className="flex w-2/3 items-center justify-between">
            <span className="text-grey-dark">
              {t(sluggify(lineItem.message))}
            </span>
            {pod && (
              <span>
                <a onClick={handlePODClick(pod.key)} target="_blank">
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <i className={getFileIcon(pod.key)} />
                      </TooltipTrigger>
                      <TooltipContent side="top">
                        {t('ATTACHED_WITH_VALUE', {
                          value: moment(pod.created_at).format('L LT'),
                        })}
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </a>
              </span>
            )}
            <div>
              {lineItem.orphaned && (
                <div className="inline">
                  <span className="flex justify-center inline-block w-full px-2 py-1 ml-4 text-xs font-bold text-red-800 rounded bg-red-50">
                    {t('MISMATCH')}
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="px-1 text-sm text-red-700 inline-flex">
                            <Info className="h-4 w-4" />
                          </span>
                        </TooltipTrigger>
                        <TooltipContent side="right">
                          {t('LINE_ITEM_EQUIPMENT_NO_LONGER_PRESENT')}
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </span>
                </div>
              )}
              {lineItem.equipment && (
                <span className="flex justify-center inline-block w-full px-2 py-1 ml-4 text-xs font-bold text-white rounded bg-green">
                  {lineItem.equipment}
                </span>
              )}
              {!lineItem.orphaned && !lineItem.equipment && (
                <span className="flex justify-center inline-block w-full px-2 py-1 ml-4 text-xs font-bold text-red-800 rounded bg-red-50">
                  {t('UNAPPLIED')}
                </span>
              )}
            </div>
          </div>
          {chargeCountMap[lineItem.type] > 1 && (
            <div className="flex w-1/4 font-semibold ml-4">
              <p className="mr-2 my-auto">{t('GROUP')}</p>
              <CargomaticSwitch
                className="my-auto"
                disabled={!selected.includes(lineItem._id)}
                height={16}
                width={32}
                uncheckedIcon={false}
                checkedIcon={false}
                checked={lineItem.grouped}
                onChange={(groupCharge) => onGroupItem(groupCharge, lineItem)}
              />
            </div>
          )}
        </div>
      </div>
      <span className="ml-4 text-lg text-grey-dark">
        {formatMoney(lineItem.value, lineItem?.currency)}
      </span>
    </div>
  );
};

export default LineItem;
